/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import { all, takeLeading, put } from 'redux-saga/effects';
import { SagaIterator } from 'redux-saga';
import {
  ActionRequest,
  ActionSuccess,
  CollectionEntitlement,
  ActionFail,
  XError,
  Constants,
  EntitlementApi,
  Ducks,
} from '@wit/vtv-x-foundation-lib';
import { StoreState } from 'store/store';

export enum ActionTypes {
  REQUEST = 'entitlements/collection/REQUEST',
  SUCCESS = 'entitlements/collection/SUCCESS',
  FAIL = 'entitlements/collection/FAIL',
}

type Request = ActionRequest<ActionTypes.REQUEST>;
type Success = ActionSuccess<ActionTypes.SUCCESS, CollectionEntitlement[]>;
type Fail = ActionFail<ActionTypes.FAIL>;

type Actions = Request | Success | Fail;

const Actions = {
  request: (): Request => ({ type: ActionTypes.REQUEST }),
  success: (payload: CollectionEntitlement[]): Success => ({ type: ActionTypes.SUCCESS, payload }),
  fail: (error: XError): Fail => ({ type: ActionTypes.FAIL, error }),
};

export const ActionCreators = {
  request: (): Request => Actions.request(),
};

export interface ReducerState {
  data: CollectionEntitlement[];
  error?: XError;
}

const initialState: ReducerState = {
  data: [],
};

export default function channelsReducer(state = initialState, action: Actions): ReducerState {
  switch (action.type) {
    case ActionTypes.REQUEST:
      return {
        ...state,
      };
    case ActionTypes.SUCCESS: {
      return {
        data: action.payload,
      };
    }
    case ActionTypes.FAIL:
      return {
        ...state,
        error: action.error,
      };
    default:
      return state;
  }
}

// SELECTORS
export const Selectors = {
  getCollectionEntitlements: (state: StoreState): CollectionEntitlement[] => {
    return state.entitlements.collection.data;
  },
  getError: (state: StoreState): XError | undefined => state.entitlements.collection.error,
};

export const PAGINATED_ENTITLEMENTS_NAMESPACE = 'ENTITLEMENTS_COLLECTION';

function* requestCollectionEntitlementsSaga(): SagaIterator {
  try {
    const result: CollectionEntitlement[] = yield Ducks.Pagination.callCompletedPaginatedApi(
      PAGINATED_ENTITLEMENTS_NAMESPACE,
      Constants.ENTITLEMENTS_API_PAGE_SIZE,
      EntitlementApi.listCollectionHouseholdEntitlements
    );

    yield put(Actions.success(result));
  } catch (error) {
    yield put(Actions.fail(XError.fromError(error)));
  }
}

export function* saga(): SagaIterator {
  yield all([takeLeading(ActionTypes.REQUEST, requestCollectionEntitlementsSaga)]);
}
